import MatchSwitch from '@components/common/matchSwitch';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { useBlindCompanies, useProfilesType } from '@repository/profiles/useProfile';
import { MATCH_CONFIG_STATUS_QUERY_KEY, useGetMatchConfigStatus, useSaveJobSearchStatus } from '@repository/match';
import BlindCompanyListModal from './blindCompanyListModal';
import MatchingStatusModal from './matchingStatusModal';
import PreferenceModal from './PreferenceModal';
import { JobSearchStatusMap, JobSearchStatusType } from '@domain/constant/match/jobSearchStatus';
import useModals from '@utils/hooks/useModals';

import { queryClient } from '@utils/queryClient';
import classnames from 'classnames/bind';
import styles from './matchProposal.module.scss';
const cx = classnames.bind(styles);

const MatchProposal = () => {
  const { closeModal, isVisible, openModal } = useModals([
    'PREFERENCE_INFO',
    'MATCHING_STATUS',
    'BLIND_COMPANY_LIST',
  ] as const);

  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;

  const { data: blindCompaniesData, refetch } = useBlindCompanies(isLogin);
  const { data: matchConfigStatus } = useGetMatchConfigStatus(isLogin);
  const { data: profilesTypeData } = useProfilesType(isLogin);
  const { mutate: saveJobSearchStatus } = useSaveJobSearchStatus(() => {
    queryClient.refetchQueries(MATCH_CONFIG_STATUS_QUERY_KEY);
  });

  const onClickConfirm = (jobSearchStatus: JobSearchStatusType) => {
    saveJobSearchStatus({ jobSearchStatus });
  };

  if (!matchConfigStatus || !profilesTypeData) return null;

  const { matchingConfigYn } = matchConfigStatus;

  return (
    <section className={cx('container')}>
      {/* 제안 받기/안받기 스위치 | 동의문/매칭설정/매칭취소 모달 */}
      <div className={cx('switchArea')}>
        <div className={cx('settingText')}>매칭 설정</div>
        <MatchSwitch />
      </div>

      {/* 구직 상태 설정 / 프로필 열람 제한 기업 설정 */}
      {matchingConfigYn && (
        <div className={cx('matchingConfigArea')}>
          <div className={cx('matchingConfigWrapper')}>
            <div className={cx('matchingConfigUpperArea')}>
              <div className={cx('matchingConfigText')}>선호 정보</div>
              <a role="button" className={cx('button')} onClick={() => openModal('PREFERENCE_INFO')}>
                변경
              </a>
            </div>
          </div>

          <div className={cx('matchingConfigWrapper')}>
            <div className={cx('matchingConfigUpperArea')}>
              <div className={cx('matchingConfigText')}>구직 상태</div>
              <a role="button" className={cx('button')} onClick={() => openModal('MATCHING_STATUS')}>
                변경
              </a>
            </div>
            <div className={cx('matchingConfigLowerArea')}>{JobSearchStatusMap[matchConfigStatus.jobSearchStatus]}</div>
          </div>

          <div className={cx('matchingConfigWrapper')}>
            <div className={cx('matchingConfigUpperArea')}>
              <div className={cx('matchingConfigText')}>프로필 열람 제한 기업</div>
              <a role="button" className={cx('button')} onClick={() => openModal('BLIND_COMPANY_LIST')}>
                변경
              </a>
            </div>
            {blindCompaniesData?.blindCompaniesText && (
              <div className={cx('matchingConfigLowerArea')}>{blindCompaniesData.blindCompaniesText}</div>
            )}
          </div>
        </div>
      )}

      {/* 선호 정보 모달 */}
      <PreferenceModal visible={isVisible('PREFERENCE_INFO')} onClose={closeModal} />

      {/* 구직 상태 모달 */}
      <MatchingStatusModal
        visible={isVisible('MATCHING_STATUS')}
        onClose={closeModal}
        onClickConfirm={onClickConfirm}
        jobSearchStatus={matchConfigStatus.jobSearchStatus}
      />

      {/* 프로필 열람 제한 기업 모달 */}
      <BlindCompanyListModal
        visible={isVisible('BLIND_COMPANY_LIST')}
        onClose={closeModal}
        blindCompanies={blindCompaniesData?.blindCompanies ?? []}
        refetch={refetch}
      />
    </section>
  );
};

export default MatchProposal;
